import {
  ICreateFlashcardCollection,
  ICreateFlashcardCollectionWithMovedFlashcard,
  IFlashcardCollection,
  IUpdateFlashcardCollection
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { sdk } from '@/services';

const FlashcardCollectionService = sdk.getService('FlashcardCollectionService');

export const useMutateFlashcardsCollection = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { t } = useTranslation('FlashcardsCommonTranslations');
  const navigateTo = useNavigate();

  const onSuccess = () =>
    queryClient.invalidateQueries({
      queryKey: ['flashcards']
    });

  const { mutate: createFlashcardCollection, isPending: isLoading } = useMutation({
    mutationFn: (flashcardCollectionData: ICreateFlashcardCollection) =>
      FlashcardCollectionService.createFlashCardCollection(flashcardCollectionData),
    onSuccess: (flashcardCollection: IFlashcardCollection) => {
      onSuccess();
      message.success(t('FLASHCARD_COLLECTION_CREATED_SUCCESSFULLY'));
      navigateTo(
        `/tests/flashcards/collection/${flashcardCollection._id}?openCreateFlashcard=true`
      );
    }
  });

  const {
    mutate: createFlashcardCollectionWithMovedFlashcard,
    isPending: isLoadingWithMovedFlashcard
  } = useMutation({
    mutationFn: ({
      flashcardCollectionData,
      collectionId
    }: ICreateFlashcardCollectionWithMovedFlashcard) =>
      FlashcardCollectionService.createFlashcardCollectionWithMovedFlashcard(
        flashcardCollectionData,
        collectionId
      ),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_COLLECTION_CREATED_SUCCESSFULLY'));
    }
  });

  const { mutate: updateFlashcardCollection, isPending: isUpdating } = useMutation({
    mutationFn: (flashcardCollectionData: IUpdateFlashcardCollection) =>
      FlashcardCollectionService.updateFlashCardCollection(flashcardCollectionData),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_COLLECTION_UPDATED_SUCCESSFULLY'));
    }
  });

  const { mutate: removeFlashcardCollection, isPending: isRemoving } = useMutation({
    mutationFn: (flashcardCollectionId: string) =>
      FlashcardCollectionService.removeFlashCardCollection(flashcardCollectionId),
    onSuccess: () => {
      onSuccess();
      message.success(t('FLASHCARD_COLLECTION_REMOVED_SUCCESSFULLY'));
    }
  });

  return {
    isWorking: isLoading || isUpdating || isRemoving || isLoadingWithMovedFlashcard,
    createFlashcardCollection,
    createFlashcardCollectionWithMovedFlashcard,
    updateFlashcardCollection,
    removeFlashcardCollection
  };
};
